/**
 * ServiceTabs — extracted "Grocery / Food / Rides" tab strip. Tap routes
 * out to the relevant vertical's home; "Grocery" stays put.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, UtensilsCrossed, Car, Bike } from 'lucide-react';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';
import { css } from '../../design';

export type ServiceTab = 'grocery' | 'food' | 'taxi' | 'auto' | 'bike';

interface TabConfig {
  key: ServiceTab;
  label: string;
  icon: React.ReactNode;
  color: string;
  flag: string;
  onClick: (nav: ReturnType<typeof useNavigate>) => void;
}

const ALL: TabConfig[] = [
  { key: 'grocery', label: 'Grocery',     icon: <ShoppingBag size={20} />,    color: 'var(--pm-color-grocery)', flag: 'stores', onClick: () => {} },
  { key: 'food',    label: 'Food',        icon: <UtensilsCrossed size={20} />,color: 'var(--pm-color-food)',    flag: 'food',   onClick: (n) => n('/restaurants') },
  { key: 'taxi',    label: 'Taxi',        icon: <Car size={20} />,            color: 'var(--pm-color-rides)',   flag: 'rides',  onClick: (n) => n('/rides/taxi') },
  { key: 'auto',    label: 'Auto',        icon: <Car size={18} />,            color: 'var(--pm-color-rides)',   flag: 'rides',  onClick: (n) => n('/rides/auto') },
  { key: 'bike',    label: 'Bike',        icon: <Bike size={20} />,           color: '#8B5CF6',                  flag: 'rides',  onClick: (n) => n('/rides/bike') },
];

export interface ServiceTabsProps {
  activeKey: ServiceTab;
  onChange?: (k: ServiceTab) => void;
}

const ServiceTabs: React.FC<ServiceTabsProps> = ({ activeKey, onChange }) => {
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const visible = ALL.filter((t) => isEnabled(t.flag));

  if (visible.length <= 1) return null;

  return (
    <div
      role="tablist"
      aria-label="Service categories"
      style={{
        position: 'sticky',
        top: 'var(--pm-navbar-height, 88px)',
        zIndex: 5,
        background: css.color.surface,
        borderBottom: `1px solid ${css.color.border}`,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          gap: 4,
          maxWidth: 1280,
          margin: '0 auto',
          padding: '8px 8px 0',
        }}
      >
        {visible.map((tab) => {
          const active = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={active}
              onClick={() => {
                onChange?.(tab.key);
                tab.onClick(navigate);
              }}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                padding: '8px 4px 10px',
                background: 'transparent',
                border: 'none',
                borderBottom: active
                  ? `3px solid ${tab.color}`
                  : '3px solid transparent',
                color: active ? tab.color : css.color.textMuted,
                cursor: 'pointer',
                transition: 'all 200ms ease',
                fontWeight: active ? 700 : 600,
                fontSize: 12,
                minWidth: 0,
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceTabs;

